import { useMemo, useState } from "react";
import dayjs, { Dayjs } from "dayjs";
import "dayjs/locale/th";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import Typography from "@mui/material/Typography";
import EventBusyIcon from "@mui/icons-material/EventBusy";
import { SURFACE as S } from "../common/designTokens";
import { API_DATE, isSunday, normalizeHolidays } from "./visitFrequency";

dayjs.locale("th");

const WEEKDAY_HEADER = ["จ.", "อ.", "พ.", "พฤ.", "ศ.", "ส.", "อา."];
const SUNDAY_COLOR = "#DC4A4A";
const HOLIDAY_BG = "#FDECEC";

interface HolidayPickerProps {
  dateFrom: Dayjs;
  dateTo: Dayjs;
  holidays: string[];
  onChange: (holidays: string[]) => void;
  disabled?: boolean;
}

/**
 * วันหยุดเพิ่มเติม — the days inside the range that should not count as working days.
 * Sundays are always off and cannot be ticked; every other day in the range toggles.
 * The draft is only applied on ใช้วันหยุดนี้, so one refetch covers a whole batch of clicks.
 */
export default function HolidayPicker({
  dateFrom,
  dateTo,
  holidays,
  onChange,
  disabled = false,
}: Readonly<HolidayPickerProps>) {
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState<string[]>([]);

  const months = useMemo(() => {
    const list: Dayjs[] = [];
    let cursor = dateFrom.startOf("month");
    const last = dateTo.startOf("month");
    while (!cursor.isAfter(last, "month")) {
      list.push(cursor);
      cursor = cursor.add(1, "month");
    }
    return list;
  }, [dateFrom, dateTo]);

  const openDialog = () => {
    setDraft(normalizeHolidays(holidays, dateFrom, dateTo));
    setOpen(true);
  };

  const toggle = (date: string) =>
    setDraft((current) =>
      current.includes(date) ? current.filter((d) => d !== date) : [...current, date],
    );

  const apply = () => {
    onChange(normalizeHolidays(draft, dateFrom, dateTo));
    setOpen(false);
  };

  return (
    <>
      <Button
        variant="outlined"
        color="inherit"
        startIcon={<EventBusyIcon />}
        onClick={openDialog}
        disabled={disabled}
        fullWidth
        sx={{ height: 40, justifyContent: "flex-start", borderColor: S.line, color: S.tx2 }}
      >
        {holidays.length > 0 ? `วันหยุดเพิ่มเติม ${holidays.length} วัน` : "ไม่มีวันหยุดเพิ่มเติม"}
      </Button>

      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>เลือกวันหยุดเพิ่มเติม</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" color="text.secondary" mb={2}>
            วันอาทิตย์เป็นวันหยุดเสมอ (สีแดง) — คลิกวันอื่นในช่วงที่เลือกเพื่อกำหนดเป็นวันหยุด
            (เช่น วันหยุดนักขัตฤกษ์) วันหยุดจะไม่ถูกนับเป็นวันทำงาน แต่ถ้ามีการเข้าร้านในวันนั้น
            ยังนับเป็นวันที่เข้า
          </Typography>
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 3 }}>
            {months.map((month) => (
              <MonthGrid
                key={month.format("YYYY-MM")}
                month={month}
                dateFrom={dateFrom}
                dateTo={dateTo}
                selected={draft}
                onToggle={toggle}
              />
            ))}
          </Box>
        </DialogContent>
        <DialogActions sx={{ justifyContent: "space-between", px: 3 }}>
          <Typography variant="body2" color="text.secondary">
            เลือกไว้ {draft.length} วัน
          </Typography>
          <Box display="flex" gap={1}>
            <Button onClick={() => setDraft([])} disabled={draft.length === 0}>
              ล้างทั้งหมด
            </Button>
            <Button onClick={() => setOpen(false)}>ยกเลิก</Button>
            <Button variant="contained" onClick={apply}>
              ใช้วันหยุดนี้
            </Button>
          </Box>
        </DialogActions>
      </Dialog>
    </>
  );
}

interface MonthGridProps {
  month: Dayjs;
  dateFrom: Dayjs;
  dateTo: Dayjs;
  selected: string[];
  onToggle: (date: string) => void;
}

function MonthGrid({ month, dateFrom, dateTo, selected, onToggle }: Readonly<MonthGridProps>) {
  const lead = (month.day() + 6) % 7; // Monday-first offset
  const cells: (Dayjs | null)[] = [
    ...Array.from({ length: lead }, () => null),
    ...Array.from({ length: month.daysInMonth() }, (_, i) => month.date(i + 1)),
  ];

  return (
    <Box sx={{ width: 230 }}>
      <Typography sx={{ fontSize: 14, fontWeight: 600, textAlign: "center", mb: 1 }}>
        {month.format("MMMM")} {month.year() + 543}
      </Typography>
      <Box sx={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)" }}>
        {WEEKDAY_HEADER.map((w, i) => (
          <Box
            key={w}
            sx={{
              fontSize: 11,
              textAlign: "center",
              pb: 0.5,
              fontWeight: 600,
              color: i === 6 ? SUNDAY_COLOR : S.tx3,
            }}
          >
            {w}
          </Box>
        ))}
        {cells.map((d, i) => {
          if (!d) return <Box key={`blank-${i}`} sx={{ height: 30 }} />;
          const key = d.format(API_DATE);
          const inRange = !d.isBefore(dateFrom, "day") && !d.isAfter(dateTo, "day");
          const sunday = isSunday(d);
          const picked = selected.includes(key);
          const clickable = inRange && !sunday;
          return (
            <Box
              key={key}
              component="button"
              type="button"
              disabled={!clickable}
              aria-pressed={picked}
              aria-label={`${d.format("D MMMM")} ${d.year() + 543}${sunday ? " (วันอาทิตย์)" : ""}`}
              onClick={() => onToggle(key)}
              sx={{
                height: 30,
                m: "1px",
                border: "1px solid",
                borderColor: picked ? SUNDAY_COLOR : "transparent",
                borderRadius: "6px",
                font: "inherit",
                fontSize: 12.5,
                fontWeight: picked ? 700 : 400,
                cursor: clickable ? "pointer" : "default",
                color: !inRange ? S.txDisabled : sunday || picked ? SUNDAY_COLOR : S.tx,
                bgcolor: inRange && (sunday || picked) ? HOLIDAY_BG : "transparent",
                "&:hover:not(:disabled)": { borderColor: SUNDAY_COLOR },
              }}
            >
              {d.date()}
            </Box>
          );
        })}
      </Box>
    </Box>
  );
}
